/*
 * Header predictive search dropdown.
 *
 * 2026-08-15 — Rewritten to use Shopify's native predictive search.
 *
 * This file previously called a third-party search provider's autocomplete /
 * suggest / trending endpoints using a site ID inherited from
 * megaofficesupplies.com.au. That returned the other store's catalogue and
 * produced /products/... links that 404 on this store. Every third-party code
 * path has been removed; nothing outside Shopify is contacted.
 *
 * Data source is now the Section Rendering API:
 *   /search/suggest?q=<term>&section_id=predictive-search&resources[type]=...
 * which returns sections/predictive-search.liquid rendered with
 * predictive_search.resources. That section emits exactly the markup, classes
 * and swiper structure this script already expects, so the dropdown looks
 * unchanged — only the data source differs.
 *
 * The empty-query state is rendered server-side into
 * <template data-predictive-search-initial> in snippets/header-search.liquid
 * from the header section's own popular_searches / recommended_products
 * settings. No third-party request is made for trending terms.
 */

const PREDICTIVE_SEARCH_SECTION_ID = "predictive-search";
const PREDICTIVE_SEARCH_DEFAULT_LIMIT = 6;
const PREDICTIVE_SEARCH_DEFAULT_TYPES = "product,collection,query,article,page";
// Part codes matter on this store ("99012", "SD18051"), so search variant
// SKU and barcode in addition to Shopify's defaults.
const PREDICTIVE_SEARCH_FIELDS =
	"title,product_type,variants.title,variants.sku,variants.barcode,vendor";

function predictiveSearchBaseUrl() {
	return (window.routes && window.routes.predictive_search_url) || "/search/suggest";
}

function buildPredictiveSearchUrl(searchTerm, options) {
	const opts = options || {};
	const limit = Number.isFinite(opts.limit) && opts.limit > 0 ? opts.limit : PREDICTIVE_SEARCH_DEFAULT_LIMIT;
	const params = new URLSearchParams();
	params.set("q", searchTerm);
	params.set("section_id", PREDICTIVE_SEARCH_SECTION_ID);
	params.set("resources[type]", opts.types || PREDICTIVE_SEARCH_DEFAULT_TYPES);
	// Shopify caps resources[limit] at 10.
	params.set("resources[limit]", String(Math.min(limit, 10)));
	params.set("resources[limit_scope]", "each");
	params.set("resources[options][unavailable_products]", "last");
	params.set("resources[options][fields]", PREDICTIVE_SEARCH_FIELDS);
	return `${predictiveSearchBaseUrl()}?${params.toString()}`;
}

/*
 * The Section Rendering API wraps the section in
 * <div id="shopify-section-predictive-search">. Unwrap it so the injected
 * markup matches what the dropdown CSS and the header category filter expect.
 */
function extractRenderedSection(html) {
	try {
		const doc = new DOMParser().parseFromString(html, "text/html");
		const wrapper = doc.getElementById(`shopify-section-${PREDICTIVE_SEARCH_SECTION_ID}`);
		if (wrapper) return wrapper.innerHTML;
	} catch (error) {
		// Fall through and use the raw response.
	}
	return html;
}

const PREDICTIVE_PRODUCTS_TWO_UP_MQ = "(max-width: 989px)";

function updatePredictiveProductsNextNav(swiper, nextEl) {
	if (!nextEl || !swiper?.slides?.length) return;

	const twoUp = window.matchMedia(PREDICTIVE_PRODUCTS_TWO_UP_MQ).matches;
	const slideCount = swiper.slides.length;
	const atLastPair =
		slideCount <= 2 || swiper.activeIndex >= slideCount - 2;
	const hideNext = twoUp && atLastPair;

	nextEl.classList.toggle("swiper-button-disabled", hideNext);
	nextEl.toggleAttribute("disabled", hideNext);
}

class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");

		// Native predictive search tuning. Override per instance with
		// data-results-limit / data-resource-types on <predictive-search>.
		const parsedLimit = parseInt(this.dataset?.resultsLimit || "", 10);
		this.resultsLimit = Number.isFinite(parsedLimit) && parsedLimit > 0
			? parsedLimit
			: PREDICTIVE_SEARCH_DEFAULT_LIMIT;
		this.resourceTypes =
			this.dataset?.resourceTypes || PREDICTIVE_SEARCH_DEFAULT_TYPES;

		this.calcHeight();
		this.setupEventListeners();
	}

	calcHeight() {
		this.results = document.querySelector(".predictive-search__result-group");
		this.resultsTabs = document.querySelector(
			".predictive-search__result-tabs",
		);
		if (this.results && this.resultsTabs) {
			this.results.style.setProperty(
				"--search-height",
				`${parseInt(this.resultsTabs.getBoundingClientRect().bottom, 10) + 100}px`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));

		// Track interaction timestamp to prevent search closing on focusout
		// Uses capture phase so it fires BEFORE Swiper or any child can stopPropagation
		// Swiper uses pointer events, so we need pointerdown as well
		this._lastInteractionTime = 0;
		const updateInteraction = () => {
			this._lastInteractionTime = Date.now();
		};
		this.addEventListener("mousedown", updateInteraction, true);
		this.addEventListener("touchstart", updateInteraction, true);
		this.addEventListener("pointerdown", updateInteraction, true);
		this.addEventListener("click", updateInteraction, true);
		// Reset timestamp on mouse/pointer up so subsequent outside clicks aren't blocked
		document.addEventListener("pointerup", () => {
			setTimeout(() => { this._lastInteractionTime = 0; }, 200);
		});
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this._initialHydrated = false;
			if (this.predictiveSearchResults) {
				this.predictiveSearchResults.innerHTML = "";
			}
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			this.maybeHydrateInitial();
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut(event) {
		// Check if focus moved to element inside this component
		if (event.relatedTarget && this.contains(event.relatedTarget)) {
			return;
		}

		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		} else {
			setTimeout(() => {
				// If interaction was recent (slider drag/click inside), refocus input
				const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
				const timeSinceInteraction = Date.now() - this._lastInteractionTime;
				if (timeSinceInteraction < 400) {
					this.input.focus();
					return;
				}

				if (!isIOS && !this.contains(document.activeElement)) {
					this.close(true);
				}
			}, 50);
		}
	}

	onBlur() {
		// this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.close(true);
				this.closeResults(true);
				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			this.initProductsSlider();
			this.calcHeight();
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		// Drop any request still in flight so a slow earlier keystroke cannot
		// overwrite the results for the term the shopper is actually on.
		this.abortController.abort();
		this.abortController = new AbortController();
		const signal = this.abortController.signal;

		const url = buildPredictiveSearchUrl(searchTerm, {
			limit: this.resultsLimit,
			types: this.resourceTypes,
		});

		fetch(url, { signal })
			.then((response) => {
				if (!response.ok) {
					const error = new Error(`Predictive search failed: ${response.status}`);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = extractRenderedSection(text);

				this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
					predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
				});

				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				this.initProductsSlider();
				this.calcHeight();
			})
			.catch((error) => {
				if (error?.code === 20 || error?.name === "AbortError") {
					return;
				}
				this.close();
				throw error;
			});

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	/*
	 * Empty-query state. Uses the server-rendered
	 * <template data-predictive-search-initial> (popular searches + the header
	 * section's recommended products). No network request, no third party.
	 */
	maybeHydrateInitial() {
		if (this.dataset.hydrateInitial === "false") return;
		if (!this.predictiveSearchResults) return;

		// First open: inject once and set _initialHydrated. Closing only removes
		// [open] (see closeResults); markup usually remains. On refocus we must
		// re-open the panel — otherwise this returns early and the dropdown stays
		// display:none.
		if (this._initialHydrated) {
			const hasInitialMarkup = this.querySelector(
				"#predictive-search-results-groups-wrapper",
			);
			if (hasInitialMarkup) {
				this.setAttribute("results", true);
				this.setLiveRegionResults();
				this.open();
				this.initProductsSlider();
				this.calcHeight();
			}
			return;
		}

		const template = this.querySelector(
			"template[data-predictive-search-initial]",
		);
		const markup = template?.innerHTML?.trim();
		if (!markup) return;

		this._initialHydrated = true;

		this.renderSearchResults(markup);
		this.clickSearchTabs();
		this.initProductsSlider();
		this.calcHeight();
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		if (!this.statusElement) return;
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement?.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		// Destroy recommended slider before injecting results to prevent event conflicts
		this.destroyRecommendedSlider();
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	initProductsSlider() {
		if (typeof Swiper === "undefined") return;

		// Destroy previous instance to avoid duplicates on re-render
		if (this._productsSwiper) {
			this._productsSwiper.destroy(true, true);
			this._productsSwiper = null;
		}

		const slider = this.querySelector(".predictive-search-products-slider.swiper");
		if (!slider) return;

		const container = slider.closest(".predictive-search-slider-container");
		const nextBtn = container?.querySelector(".predictive-slider-next");

		// Ensure the list is visible before Swiper calculates widths
		const list = slider.querySelector(".predictive-search__results-list_products");
		if (list) list.classList.add("active");

		const syncNextNav = (swiper) =>
			updatePredictiveProductsNextNav(swiper, nextBtn);

		this._productsSwiper = new Swiper(slider, {
			slidesPerView: "auto",
			spaceBetween: 20,
			watchOverflow: true,
			observer: true,
			observeParents: true,
			resizeObserver: true,
			updateOnWindowResize: true,
			navigation: container ? {
				nextEl: nextBtn,
				prevEl: container.querySelector(".predictive-slider-prev"),
			} : false,
			on: {
				init(swiper) {
					requestAnimationFrame(() => {
						swiper.update();
						syncNextNav(swiper);
					});
				},
				slideChange(swiper) {
					syncNextNav(swiper);
				},
				resize(swiper) {
					swiper.update();
					syncNextNav(swiper);
				},
			},
		});
	}

	destroyRecommendedSlider() {
		const slider = this.querySelector(".predictive-search-recommended-slider.swiper");
		if (slider && slider.swiper) {
			slider.swiper.destroy(true, true);
		}
	}

	initRecommendedSlider() {
		if (typeof Swiper === "undefined") return;

		const slider = this.querySelector(".predictive-search-recommended-slider.swiper");
		if (!slider || slider.classList.contains("swiper-initialized")) return;

		const container = slider.closest(".predictive-search-slider-container");

		new Swiper(slider, {
			slidesPerView: "auto",
			spaceBetween: 20,
			watchOverflow: true,
			observer: true,
			observeParents: true,
			resizeObserver: true,
			updateOnWindowResize: true,
			navigation: container ? {
				nextEl: container.querySelector(".predictive-slider-next"),
				prevEl: container.querySelector(".predictive-slider-prev"),
			} : false,
			on: {
				init(swiper) {
					requestAnimationFrame(() => swiper.update());
				},
			},
		});
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length != 0) {
			results[0].classList.add("active");
			this.querySelector(".predictive-search__result-tab")?.classList.add(
				"active",
			);
			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
			});
		}
	}

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
		// Re-init recommended slider when returning to initial state
		this.initRecommendedSlider();
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		const live = this.querySelector(
			"[data-predictive-search-live-region-count-value]",
		);
		if (live) this.setLiveRegionText(live.textContent);
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
